import React from 'react';
import { Select } from 'antd';
import { FaCirclePlus, FaCircleMinus } from 'react-icons/fa6';
import { countries } from "../shared/countrySelectInput/countries";
import AsyncSelect from 'react-select/async';
import { loadHSCodeOptions } from './estimateFormHandlers';

const EstimateFormItems = ({
    items,
    itemsCurrency,
    selectedService,
    errors,
    onInputChange,
    onServiceInputChange,
    onAntdServiceInputChange,
    onAddItem,
    onRemoveItem,
    isReadOnly,
    destinationCountry,
}) => {
    const loadHSCodeOptionsForSelect = async (inputValue) => {
        try {
            return await loadHSCodeOptions(destinationCountry, inputValue);
        } catch (error) {
            console.error("Failed to load HS codes", error);
            return [];
        }
    };

    // Handler for HS Code source change
    const handleHSCodeSourceChange = (index, source, onServiceInputChange) => {
        // Create a synthetic event for the service input change handler
        const syntheticEvent = {
            target: {
                name: `hsCodeSource-${index}`,
                value: source
            }
        };

        // Update the hsCodeSource and clear the itemHSCode
        onServiceInputChange(index, 'hsCodeSource', syntheticEvent);

        // Clear the HS Code when switching sources
        const clearHSCodeEvent = {
            target: {
                name: `itemHSCode-${index}`,
                value: ''
            }
        };
        onServiceInputChange(index, 'itemHSCode', clearHSCodeEvent);
    };

    return (
        <div className="w-full flex flex-col gap-5 mt-8">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold">Item Details</h2>
                    <FaCirclePlus className="text-[22px] text-[#03336e]" onClick={onAddItem} />
                </div>
                <div className="flex items-center gap-2">
                    <h1 className="mt-2">Currency</h1>
                    <select
                        className="p-1 w-full text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        name="itemsCurrency"
                        onChange={onInputChange}
                        value={itemsCurrency}
                        disabled={isReadOnly}
                    >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <h1 className="mt-2">Service</h1>
                    <select
                        className="p-1 w-full text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        name="selectedService"
                        onChange={onInputChange}
                        value={selectedService === "EOR & IOR" ? "IOR&EOR" : selectedService}
                        disabled={isReadOnly}
                    >
                        <option value="IOR">IOR</option>
                        <option value="EOR">EOR</option>
                        <option value="IOR&EOR">IOR and EOR</option>
                    </select>
                </div>
            </div>
            {items.map((item, index) => (
                <div key={`item-${index}`} className="md:px-0">
                    <h2 className="flex gap-3 items-center text-[16px] font-bold text-gray-500 mt-6">
                        Item Details
                        <button onClick={() => onRemoveItem(index)} disabled={isReadOnly}>
                            <FaCircleMinus />
                        </button>
                    </h2>
                    <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                        {["itemName", "quantity", "itemValue"].map(field => (
                            <div key={field} className="flex flex-col gap-1 py-2">
                                <label className="text-sm font-normal">
                                    <span className="text-red-500">*</span>{" "}
                                    {field === "itemName" ? "Item Name" : field === "quantity" ? "Quantity" : "Item Value"}
                                </label>
                                <input
                                    type={field === "itemName" ? "text" : "number"}
                                    className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${errors[`${field}-${index}`] ? "border-red-500" : "active:border-cus-blue focus:border-cus-blue"}`}
                                    onChange={e => onServiceInputChange(index, field, e)}
                                    value={item[field]}
                                    readOnly={isReadOnly}
                                    min={field === "quantity" ? "1" : "0"}
                                    step={field === "quantity" ? "1" : "any"}
                                    onKeyDown={e => {
                                        if (field !== "itemName" && ["e", "E", "+", "-"].includes(e.key) || (field === "quantity" && e.key === ".")) {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                {errors[`${field}-${index}`] && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {field === "itemName" ? "Item Name" : field === "quantity" ? "Quantity" : "Item Value"} is required
                                    </p>
                                )}
                            </div>
                        ))}
                        <div className="flex flex-col gap-1 py-2">
                            <label className="text-sm font-normal">
                                <span className="text-red-500">*</span> Country of Origin
                            </label>
                            <Select
                                showSearch
                                options={countries.map((country, idx) => ({
                                    value: country,
                                    label: country,
                                    key: idx,
                                }))}
                                onChange={value => onAntdServiceInputChange(index, "countryOrigin", value)}
                                value={item.countryOrigin}
                                style={{ width: "100%" }}
                                className={`w-full ${errors[`countryOrigin-${index}`] ? "border-red-500" : ""}`}
                                disabled={isReadOnly}
                            />
                            {errors[`countryOrigin-${index}`] && (
                                <p className="text-red-500 text-xs mt-1">Country of Origin is required</p>
                            )}
                        </div>
                        <div className="flex flex-col gap-1 py-2">
                            <label className="text-sm font-normal">
                                Do you have Item HS Code? <span className="text-red-500">*</span>
                            </label>
                            <div className={`flex gap-4 ${errors[`hasHSCode-${index}`] ? "border border-red-500 rounded-md p-2" : ""}`}>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name={`hasHSCode-${index}`}
                                        value="yes"
                                        checked={item.hasHSCode === true}
                                        onChange={e => onServiceInputChange(index, "hasHSCode", e)}
                                        disabled={isReadOnly}
                                        className="mr-2"
                                    />
                                    Yes
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name={`hasHSCode-${index}`}
                                        value="no"
                                        checked={item.hasHSCode === false}
                                        onChange={e => onServiceInputChange(index, "hasHSCode", e)}
                                        disabled={isReadOnly}
                                        className="mr-2"
                                    />
                                    No
                                </label>
                            </div>
                            {errors[`hasHSCode-${index}`] && (
                                <p className="text-red-500 text-xs mt-1">Please specify if you have an HS Code</p>
                            )}
                            {item.hasHSCode && (
                                <div className="mt-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name={`hsCodeSource-${index}`}
                                                value="database"
                                                checked={item.hsCodeSource !== 'manual'}
                                                onChange={() => handleHSCodeSourceChange(index, 'database', onServiceInputChange)}
                                                className="mr-1"
                                                disabled={isReadOnly}
                                            />
                                            Search from Database
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name={`hsCodeSource-${index}`}
                                                value="manual"
                                                checked={item.hsCodeSource === 'manual'}
                                                onChange={() => handleHSCodeSourceChange(index, 'manual', onServiceInputChange)}
                                                className="mr-1"
                                                disabled={isReadOnly}
                                            />
                                            Enter Manually
                                        </label>
                                    </div>

                                    {item.hsCodeSource === 'manual' ? (
                                        // Manual input field
                                        <input
                                            type="text"
                                            placeholder="Enter HS Code (first 6 digits must be numbers)"
                                            value={item.itemHSCode || ''}
                                            onChange={(e) => {
                                                let value = e.target.value;

                                                // Validation: first 6 characters must be digits
                                                if (value.length > 0) {
                                                    const first6 = value.substring(0, 6);
                                                    if (!/^\d{1,6}$/.test(first6)) {
                                                        // If first 6 characters are not all digits, prevent the change
                                                        return;
                                                    }
                                                }

                                                onServiceInputChange(index, "itemHSCode", {
                                                    target: { value }
                                                });
                                            }}
                                            className={`w-full p-2 text-sm border rounded-md outline-none ${errors[`itemHSCode-${index}`] ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'
                                                }`}
                                            disabled={isReadOnly}
                                        />
                                    ) : (
                                        // Database search (existing AsyncSelect)
                                        <AsyncSelect
                                            key={`${index}-${item.itemHSCode || "empty"}`}
                                            cacheOptions
                                            loadOptions={loadHSCodeOptionsForSelect}
                                            defaultOptions
                                            onChange={selectedOption =>
                                                onServiceInputChange(index, "itemHSCode", { target: { value: selectedOption ? selectedOption.value : "" } })
                                            }
                                            value={item.itemHSCode ? { value: item.itemHSCode, label: item.itemHSCode } : null}
                                            placeholder="Search HS Code (min 6 digits)"
                                            isClearable
                                            isDisabled={isReadOnly || !destinationCountry}
                                            styles={{
                                                control: base => ({
                                                    ...base,
                                                    minHeight: "32px",
                                                    height: "32px",
                                                    borderColor: errors[`itemHSCode-${index}`] ? "#ef4444" : base.borderColor,
                                                }),
                                                valueContainer: base => ({ ...base, height: "32px", padding: "0 8px" }),
                                                input: base => ({ ...base, margin: "0" }),
                                                indicatorsContainer: base => ({ ...base, height: "32px" }),
                                            }}
                                        />
                                    )}

                                    {errors[`itemHSCode-${index}`] && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {typeof errors[`itemHSCode-${index}`] === 'string'
                                                ? errors[`itemHSCode-${index}`]
                                                : "HS Code must be at least 6 digits"
                                            }
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-1 py-2">
                            <label className="text-sm font-normal">
                                Is your Item New? <span className="text-red-500">*</span>
                            </label>
                            <div className={`flex gap-4 ${errors[`itemNew-${index}`] ? "border border-red-500 rounded-md p-2" : ""}`}>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name={`itemNew-${index}`}
                                        value="yes"
                                        checked={item.itemNew === true}
                                        onChange={e => onServiceInputChange(index, "itemNew", e)}
                                        disabled={isReadOnly}
                                        className="mr-2"
                                    />
                                    Yes
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name={`itemNew-${index}`}
                                        value="no"
                                        checked={item.itemNew === false}
                                        onChange={e => onServiceInputChange(index, "itemNew", e)}
                                        disabled={isReadOnly}
                                        className="mr-2"
                                    />
                                    No
                                </label>
                            </div>
                            {errors[`itemNew-${index}`] && (
                                <p className="text-red-500 text-xs mt-1">Please specify if the item is new</p>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default EstimateFormItems;